import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { TransactionStatus, type Transaction } from '../../types/ApiTypes';
import { Badge, MonoUsername } from '../styles/TableStyle';
import {
   CloseBtn,
   Grid,
   H1,
   Label,
   Overlay,
   Panel,
   Section,
   SectionTitle,
   TitleRow,
   Value
} from '../styles/TransactionDetailStyle';
import { formatDate } from '../utils/dateHelpers';
import { formatWithCommas } from '../utils/numberHelpers';

interface TxnPanelProps {
   open: boolean;
   onClose: () => void;
   txn?: Transaction | undefined;
}

export default function TransactionDetails({ open, onClose, txn }: TxnPanelProps) {
   const [previewImage, setPreviewImage] = useState<string | null>(null);

   useEffect(() => {
      function esc(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
      if (open) window.addEventListener('keydown', esc);
      return () => window.removeEventListener('keydown', esc);
   }, [open, onClose]);

   if (!open || !txn) return null;

   const formatTransactionType = (type: string): string => {
      return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
   }

   const statusMap: Record<string, 'Completed' | 'Pending' | 'Failed'> = {
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed'
   };

   const status = statusMap[txn.status] || 'Pending';
   const isPendingOrFailed = txn.status === TransactionStatus.PENDING || txn.status === TransactionStatus.FAILED;

   const renderSummary = () => {
      if (txn.giftcardPurchase) {
         return (
            <>
               <Label>Amount</Label>
               <Value>₦{formatWithCommas(txn.amount)}</Value>
               <Label>Brand</Label>
               <Value>{txn.giftcardPurchase.brandName}</Value>
               <Label>Product</Label>
               <Value>{txn.giftcardPurchase.productName}</Value>
               <Label>Card Number</Label>
               <Value>{txn.giftcardPurchase.cardNumber}</Value>
               <Label>Pin Code</Label>
               <Value>{txn.giftcardPurchase.pinCode}</Value>
               <Label>Rate</Label>
               <Value>₦{formatWithCommas(txn.giftcardPurchase.ratePerDollar)}</Value>
            </>
         );
      }

      if (txn.giftcardSale) {
         return (
            <>
               <Label>Type</Label>
               <Value>{txn.giftcardSale.type}</Value>
               <Label>Subcategory</Label>
               <Value>{txn.giftcardSale.country} {txn.giftcardSale?.subcategoryName} (${txn.giftcardSale.minAmount}, ${txn.giftcardSale.maxAmount})</Value>
               <Label>Rate</Label>
               <Value>₦{formatWithCommas(txn.giftcardSale.ratePerDollar)}</Value>
               <Label>Amount</Label>
               <Value>₦{formatWithCommas(txn.amount)}</Value>
               {txn.giftcardSale.ecode && (
                  <>
                     <Label>Ecode</Label>
                     <Value>{txn.giftcardSale.ecode}</Value>
                  </>
               )}
               {txn.giftcardSale.rejectionReason && (
                  <>
                     <Label>Rejection Reason</Label>
                     <Value>{txn.giftcardSale.rejectionReason}</Value>
                  </>
               )}
            </>
         );
      }

      if (txn.cryptoPurchase) {
         return (
            <>
               <Label>Asset</Label>
               <Value>{txn.cryptoPurchase.currency.toUpperCase()}</Value>
               <Label>Amount</Label>
               <Value>{!txn.cryptoPurchase.amountInCrypto ? 'N/A' : formatWithCommas(txn.cryptoPurchase.amountInCrypto)}</Value>
               <Label>Rate</Label>
               <Value>₦{formatWithCommas(txn.cryptoPurchase.ratePerDollar)}</Value>
               <Label>Total Amount</Label>
               <Value>₦{formatWithCommas(txn.amount)}</Value>
            </>
         );
      }

      if (txn.cryptoSale) {
         return (
            <>
               <Label>Asset</Label>
               <Value>{txn.cryptoSale.currency.toUpperCase()}</Value>
               <Label>Amount</Label>
               <Value>{formatWithCommas(txn.cryptoSale.amountInCrypto)}</Value>
               <Label>Rate</Label>
               <Value>₦{formatWithCommas(txn.cryptoSale.ratePerDollar)}</Value>
               <Label>Total Amount</Label>
               <Value>₦{formatWithCommas(txn.amount)}</Value>
            </>
         );
      }

      if (txn.cryptoDeposit) {
         return (
            <>
               <Label>Asset</Label>
               <Value>{txn.cryptoDeposit.currency.toUpperCase()}</Value>
               <Label>Amount</Label>
               <Value>{formatWithCommas(txn.cryptoDeposit.amountInCrypto)}</Value>
               <Label>Rate</Label>
               <Value>₦{formatWithCommas(txn.cryptoDeposit.ratePerDollar)}</Value>
               <Label>Total Amount</Label>
               <Value>₦{formatWithCommas(txn.amount)}</Value>
            </>
         );
      }

      if (txn.topup) {
         return (
            <>
               <Label>Account Number</Label>
               <Value>{txn.topup.accountNumber}</Value>
               <Label>Account Name</Label>
               <Value>{txn.topup.accountName}</Value>
               <Label>Bank Name</Label>
               <Value>Safehaven MFB</Value>
               <Label>Amount</Label>
               <Value>₦{formatWithCommas(txn.amount)}</Value>
            </>
         );
      }

      if (txn.withdrawal) {
         return (
            <>
               <Label>Account Number</Label>
               <Value>{txn.withdrawal.accountNumber}</Value>
               <Label>Account Name</Label>
               <Value>{txn.withdrawal.accountName}</Value>
               <Label>Bank Name</Label>
               <Value>{txn.withdrawal.bankName}</Value>
               <Label>Amount</Label>
               <Value>₦{formatWithCommas(txn.amount)}</Value>
            </>
         );
      }

      return (
         <>
            <Label>Amount</Label>
            <Value>₦{formatWithCommas(txn.amount)}</Value>
         </>
      );
   };

   return (
      <Overlay onClick={onClose}>
         <Panel onClick={e => e.stopPropagation()}>
            <TitleRow>
               <H1>Transaction Details</H1>
               <CloseBtn aria-label="Close" onClick={onClose}><Icon icon="material-symbols:close" width={20} /></CloseBtn>
            </TitleRow>

            <Section>
               <Grid>
                  <Label>Transaction ID</Label>
                  <Value>{txn.transactionId}</Value>
                  <Label>Transaction Reference</Label>
                  <Value>{txn.reference || 'N/A'}</Value>
                  <Label>Transaction Type</Label>
                  <Value>{formatTransactionType(txn.type)}</Value>
                  <Label>Date</Label>
                  <Value>{formatDate(txn.date)}</Value>
                  <Label>Status</Label>
                  <Value>
                     <Badge variant={status === 'Completed' ? 'success' : status === 'Pending' ? 'pending' : 'failed'}>
                        {status}
                     </Badge>
                  </Value>
               </Grid>
            </Section>

            <Section>
               <SectionTitle>User info</SectionTitle>
               <Grid>
                  <Label>Username</Label>
                  <Value><MonoUsername style={{ fontSize: 14 }}>@{txn.username}</MonoUsername></Value>
                  <Label>Balance before</Label>
                  <Value>₦{formatWithCommas(txn.balanceBefore)}</Value>
                  <Label>Balance after</Label>
                  <Value>{isPendingOrFailed ? 'N/A' : `₦${formatWithCommas(txn.balanceAfter)}`}</Value>
               </Grid>
            </Section>

            <Section>
               <SectionTitle>Summary</SectionTitle>
               <Grid>
                  {renderSummary()}
               </Grid>
            </Section>

            {txn.giftcardSale?.cardUrls && txn.giftcardSale.cardUrls.length > 0 && (
               <Section>
                  <SectionTitle>Card images</SectionTitle>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                     {txn.giftcardSale.cardUrls.map((url, i) => (
                        <div key={i} style={{ width: 56, height: 56, borderRadius: 6, overflow: 'hidden', background: '#F5F5F6', position: 'relative', flexShrink: 0, cursor: 'pointer' }} onClick={() => setPreviewImage(url)}>
                           <img src={url} alt={`card-${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                           <div style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: 100, background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                 <g clipPath="url(#clip0_890_8320)">
                                    <path d="M3.00033 5L0.666992 7.33333M0.666992 7.33333H2.61937M0.666992 7.33333V5.38097" stroke="#3F3F45" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M5 2.99935L7.33333 0.666016M7.33333 0.666016H5.38097M7.33333 0.666016V2.6184" stroke="#3F3F45" strokeLinecap="round" strokeLinejoin="round" />
                                 </g>
                                 <defs>
                                    <clipPath id="clip0_890_8320">
                                       <rect width="8" height="8" fill="white" />
                                    </clipPath>
                                 </defs>
                              </svg>
                           </div>
                        </div>
                     ))}
                  </div>
               </Section>
            )}
         </Panel>

         {previewImage && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}>
               <button onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: 28, width: 40, height: 40, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
               <img src={previewImage} alt="Preview" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 8 }} onClick={e => e.stopPropagation()} />
            </div>
         )}
      </Overlay>
   )
}
